import { useMutation, useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { GET_ME } from '../graphql/getMe';
import {
  Box,
  CircularProgress,
  Grid2 as Grid,
  Typography,
  Button,
  LinearProgress,
} from '@mui/material';
import { OrganCard } from '../components/OrganCard';
import {
  CreateQuizMutation,
  GetAllInProgressQuizzesForUserQuery,
  GetUserByEmailQuery,
} from '../__generated__/graphql';
import { TAGS } from '../types/Tag';
import { CommonStyles } from '../styles/CommonStyles';
import { DashboardStyles } from '../styles/DashboardStyles';
import { CREATE_QUIZ } from '../graphql/createQuiz';
import { GET_ALL_IN_PROGRESS_QUIZZES_FOR_USER } from '../graphql/getAllInProgressQuizzesForUser';
import { GET_USER_PROGRESS_FOR_ALL_TAGS } from '../graphql/getUserProgressForAllTags';
import { GET_USER_BY_EMAIL } from '../graphql/getUserByEmail';
import { useEffect } from 'react';

interface UserProgress {
  tag: string;
  totalQuestions: number;
  correctQuestions: number;
  progress: number;
}

interface GetUserProgressForAllTagsQuery {
  getUserProgressForAllTags: UserProgress[];
}

const Dashboard = () => {
  const navigate = useNavigate();
  const { data: me, loading: meLoading } = useQuery(GET_ME);
  const userId = me?.getMe?.id;
  const userEmail = me?.getMe?.email;
  const [createQuiz, { data }] = useMutation<CreateQuizMutation>(CREATE_QUIZ);

  const { data: inProgressQuizzes, loading: inProgressQuizzesLoading } =
    useQuery<GetAllInProgressQuizzesForUserQuery>(
      GET_ALL_IN_PROGRESS_QUIZZES_FOR_USER,
    );

  const {
    data: userProgressData,
    loading: userProgressLoading,
    refetch: refetchUserProgress,
  } = useQuery<GetUserProgressForAllTagsQuery>(GET_USER_PROGRESS_FOR_ALL_TAGS);

  const {
    data: userData,
    loading: userLoading,
    refetch: refetchUserData,
  } = useQuery<GetUserByEmailQuery>(GET_USER_BY_EMAIL, {
    variables: { email: userEmail || '' },
    skip: !userEmail,
  });

  useEffect(() => {
    const handleFocus = () => {
      if (userEmail) {
        refetchUserData();
        refetchUserProgress();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [userEmail, refetchUserData, refetchUserProgress]);

  const getProgressForTag = (tag: string) => {
    if (!userProgressData?.getUserProgressForAllTags) {
      return 0;
    }

    const progressEntry = userProgressData.getUserProgressForAllTags.find(
      (entry: UserProgress) => entry.tag === tag,
    );

    return progressEntry ? progressEntry.progress : 0;
  };

  const getCompletedOrgans = () => {
    if (!userProgressData?.getUserProgressForAllTags) {
      return 0;
    }
    return userProgressData.getUserProgressForAllTags.filter(
      (entry) => entry.progress === 100,
    ).length;
  };

  const getInProgressQuizzes = () => {
    return inProgressQuizzes?.getAllInProgressQuizzesForUser.length || 0;
  };

  const handleCreateQuiz = async (tag: string) => {
    // TODO: logic to check for in progress quiz
    try {
      createQuiz({
        variables: {
          tag,
          userId,
        },
        refetchQueries: [
          { query: GET_ALL_IN_PROGRESS_QUIZZES_FOR_USER },
          { query: GET_USER_PROGRESS_FOR_ALL_TAGS },
        ],
      });
    } catch (error) {
      console.log('Error creating quiz: ', error);
      return;
    }
  };

  if (data) {
    navigate(`/quiz/${data?.createQuiz.id}`, {
      state: { email: me?.getMe?.email, tag: data?.createQuiz.tag },
    });
  }

  if (
    meLoading ||
    inProgressQuizzesLoading ||
    userProgressLoading ||
    userLoading
  ) {
    return (
      <Box sx={CommonStyles.loadingContainer}>
        <CircularProgress size={70} />
      </Box>
    );
  }

  return (
    <Box sx={DashboardStyles.container}>
      <Box sx={DashboardStyles.contentWrapper}>
        {/* Header Section */}
        <Box sx={DashboardStyles.header}>
          <Typography sx={DashboardStyles.welcomeText}>
            Welcome back, {me?.getMe?.email?.split('@')[0]}! 👋
          </Typography>
          <Typography sx={DashboardStyles.subtitle}>
            Continue your journey through Traditional Chinese Medicine
          </Typography>

          {/* Statistics Cards */}
          <Box sx={DashboardStyles.statsContainer}>
            <Box sx={DashboardStyles.statCard}>
              <Typography sx={DashboardStyles.statValue}>
                {getInProgressQuizzes()}
              </Typography>
              <Typography sx={DashboardStyles.statLabel}>
                In Progress Quizzes
              </Typography>
            </Box>
            <Box sx={DashboardStyles.statCard}>
              <Typography sx={DashboardStyles.statValue}>
                {getCompletedOrgans()}
              </Typography>
              <Typography sx={DashboardStyles.statLabel}>
                Organs Mastered
              </Typography>
            </Box>
            <Box sx={DashboardStyles.statCard}>
              <Typography sx={DashboardStyles.statValue}>
                {TAGS.length}
              </Typography>
              <Typography sx={DashboardStyles.statLabel}>
                Total Organs
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Organs Section */}
        <Box sx={DashboardStyles.organsSection}>
          <Typography sx={DashboardStyles.sectionTitle}>
            Select an organ to begin your study
          </Typography>
          <Grid container spacing={3} sx={DashboardStyles.organsGrid}>
            {TAGS.map((tag) => (
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={tag}>
                <OrganCard
                  tag={tag}
                  progress={getProgressForTag(tag)}
                  handleOnClick={() => {
                    const inProgressQuiz =
                      inProgressQuizzes?.getAllInProgressQuizzesForUser.find(
                        (quiz) => quiz.tag === tag,
                      );
                    if (inProgressQuiz) {
                      navigate(`/quiz/${inProgressQuiz.id}`, {
                        state: {
                          email: me?.getMe?.email,
                          tag: inProgressQuiz.tag,
                        },
                      });
                    } else {
                      handleCreateQuiz(tag);
                    }
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
